import { useState } from 'react';
import { toast } from 'react-toastify';
import ReactDatePicker from 'react-datepicker';

import styled from 'styled-components';
import { FaTrash } from 'react-icons/fa';

import { colors } from 'constants/colors';

import { SupplyOrderProductReceivedDetail } from 'pages/SupplyOrders/shared/types';

import { TotemPopup } from 'components/TotemPopup';
import { BasicInput } from 'components/BasicInput';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemSelect } from 'components/TotemSelect';
import { TotemLabel } from 'components/TotemLabel';
import { Loader, LoaderModeType } from 'components/Loader';

import { areExpiryDatesUnique, getSupplyOrderProductReceivedTotal } from 'pages/SupplyOrders/shared/receivedDetails';
import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';
import { getProductFullName } from 'pages/Products/utils';
import {
    useAddAdditionalSupplyOrderReceivedProductDetailsMutation,
    useGetSupplyOrderProductsToAddQuery,
    TotadminPermission,
} from 'data/__generated__';

/**
 * Déclarer des quantités reçues en plus, hors du contrôle de réception habituel.
 *
 * Le choix porte sur TOUT le catalogue du fournisseur — on peut recevoir une référence qui n'avait pas
 * été commandée. Ce catalogue est lourd : il n'est chargé qu'à l'ouverture de la fenêtre.
 */
export const AdditionalDeliveryPopup = ({
    isOpen,
    setIsOpen,
    supplyOrderId,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    supplyOrderId: string;
}) => {
    const { data: productsToAddData, loading: productsToAddLoading } = useGetSupplyOrderProductsToAddQuery({
        variables: { supplyOrderId },
        skip: !isOpen,
    });
    const supplierProducts = productsToAddData?.supplyOrderProductsToAdd ?? [];
    const [productsWithAdditionalQuantities, setProductsWithAdditionalQuantities] = useState<
        {
            productId: string;
            fullname: string;
            productReceivedDetails: SupplyOrderProductReceivedDetail[];
        }[]
    >([]);

    const [addAdditionalReceivedProductDetails, { loading }] =
        useAddAdditionalSupplyOrderReceivedProductDetailsMutation();

    function onClose() {
        setProductsWithAdditionalQuantities([]);
        setIsOpen(false);
    }

    async function handleProductDetailsUpdate() {
        if (
            productsWithAdditionalQuantities.some(
                ({ productReceivedDetails }) =>
                    !areExpiryDatesUnique(productReceivedDetails) ||
                    productReceivedDetails.some(({ quantity, brokenItems }) => quantity === 0 && brokenItems === 0),
            )
        )
            return;

        const productDetails = productsWithAdditionalQuantities.map(({ productId, productReceivedDetails }) => ({
            productId,
            productReceivedDetails,
        }));

        const { data } = await addAdditionalReceivedProductDetails({
            variables: { supplyOrderId, productDetails },
        });

        if (!data?.addAdditionalSupplyOrderReceivedProductDetails) {
            throw Error("Une erreur inconnue s'est produite");
        } else {
            toast.success('Les quantités ont bien été mises à jour !');
        }
        onClose();
    }

    function onProductSelect(productId: string | null) {
        if (productId && !productsWithAdditionalQuantities.some(({ productId: id }) => id === productId)) {
            const supplierProduct = supplierProducts.find(({ _id }) => _id === productId);

            if (!supplierProduct) return;

            const fullname = getProductFullName({
                name: supplierProduct.name,
                brand: supplierProduct.brand,
                volume: supplierProduct.volume,
            });

            setProductsWithAdditionalQuantities([
                ...productsWithAdditionalQuantities,
                {
                    productId,
                    fullname,
                    productReceivedDetails: [
                        {
                            brokenItems: 0,
                            colis: 0,
                            conditionning: supplierProduct.conditionning,
                            expiryDate: null,
                            quantity: 0,
                        },
                    ],
                },
            ]);
        }
    }

    function onProductRemove(productToRemoveId: string) {
        setProductsWithAdditionalQuantities(
            productsWithAdditionalQuantities.filter(({ productId }) => productId !== productToRemoveId),
        );
    }

    function onDetailChange({
        productIdToUpdate,
        productDetailIndex,
        productDetail,
    }: {
        productIdToUpdate: string;
        productDetailIndex: number;
        productDetail: SupplyOrderProductReceivedDetail;
    }) {
        const productWithAdditionalQuantities = productsWithAdditionalQuantities.find(
            ({ productId }) => productId === productIdToUpdate,
        );
        if (!productWithAdditionalQuantities) return;

        const updatedProductReceivedDetails = [...productWithAdditionalQuantities.productReceivedDetails];

        updatedProductReceivedDetails[productDetailIndex] = {
            ...productDetail,
            colis: Math.floor(
                productDetail.conditionning
                    ? productDetail.quantity / productDetail.conditionning
                    : productDetail.quantity,
            ),
        };

        if (!areExpiryDatesUnique(updatedProductReceivedDetails)) {
            toast.error('Vous ne pouvez avoir deux DLC identiques');
            return;
        }

        setProductsWithAdditionalQuantities(
            productsWithAdditionalQuantities.map((product) => {
                if (product.productId !== productIdToUpdate) return product;

                return {
                    ...product,
                    productReceivedDetails: updatedProductReceivedDetails,
                };
            }),
        );
    }

    function addDetail(productIdToUpdate: string) {
        const supplierProduct = supplierProducts.find(({ _id }) => _id === productIdToUpdate);
        const productWithAdditionalQuantities = productsWithAdditionalQuantities.find(
            ({ productId }) => productId === productIdToUpdate,
        );

        if (!supplierProduct || !productWithAdditionalQuantities) return;

        setProductsWithAdditionalQuantities(
            productsWithAdditionalQuantities.map((product) => {
                if (product.productId !== productIdToUpdate) return product;

                const productReceivedDetails = [
                    ...product.productReceivedDetails,
                    {
                        brokenItems: 0,
                        colis: 0,
                        conditionning: supplierProduct.conditionning,
                        expiryDate: null,
                        quantity: 0,
                    },
                ];

                if (!areExpiryDatesUnique(productReceivedDetails)) {
                    toast.error('Vous ne pouvez avoir deux DLC identiques');
                }

                return {
                    ...product,
                    productReceivedDetails: productReceivedDetails,
                };
            }),
        );
    }

    function removeDetail({
        productIdToUpdate,
        productDetailIndex,
    }: {
        productIdToUpdate: string;
        productDetailIndex: number;
    }) {
        const productWithAdditionalQuantities = productsWithAdditionalQuantities.find(
            ({ productId }) => productId === productIdToUpdate,
        );

        if (!productWithAdditionalQuantities) return;

        setProductsWithAdditionalQuantities(
            productsWithAdditionalQuantities.map((product) => {
                if (product.productId !== productIdToUpdate) return product;

                const updatedProductReceivedDetails = [
                    ...product.productReceivedDetails.slice(0, productDetailIndex),
                    ...product.productReceivedDetails.slice(productDetailIndex + 1),
                ];

                return {
                    ...product,
                    productReceivedDetails: updatedProductReceivedDetails,
                };
            }),
        );
    }

    const productsOptions =
        supplierProducts.map(({ _id, name, brand, volume }) => ({
            value: _id,
            label: getProductFullName({ name, brand, volume }),
        })) || [];

    const shouldPreventUpdate = productsWithAdditionalQuantities.some(
        ({ productReceivedDetails }) =>
            !areExpiryDatesUnique(productReceivedDetails) ||
            productReceivedDetails.some(({ quantity, brokenItems }) => quantity === 0 && brokenItems === 0),
    );

    return (
        <TotemPopup
            title="Déclarer quantités additionnelles livrées"
            isOpen={isOpen}
            setIsOpen={onClose}
            contentOverflow={productsWithAdditionalQuantities.length ? 'scroll' : 'overflow'}
        >
            <Container>
                <TotemSelect
                    dataTest="select-product"
                    label="Ajouter un produit"
                    placeholder="Sélectionner un produit (recherche par nom ou par _id)"
                    value={null}
                    options={productsOptions}
                    onChange={onProductSelect}
                />
                <>
                    {productsWithAdditionalQuantities.map(({ productId, fullname, productReceivedDetails }) => {
                        const { brokenItemsReceived, colisReceived, conditionningReceived, quantityReceived } =
                            getSupplyOrderProductReceivedTotal(productReceivedDetails);

                        return (
                            <ProductContainer key={productId}>
                                <TitleRow>
                                    <TotemLabel>{fullname}</TotemLabel>

                                    <IconContainer onClick={() => onProductRemove(productId)}>
                                        <FaTrash data-test="trash-icon" size={16} color={colors.pureWhite} />
                                    </IconContainer>
                                </TitleRow>
                                {productReceivedDetails.length ? (
                                    <ReceivedDetailsTable>
                                        <thead>
                                            <tr>
                                                <ReceivedDetailsHeaderCell>Quantité reçue</ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell>
                                                    Quantité reçue cassée
                                                </ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell>Nb. Colis reçus</ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell>DLC</ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell>Colisage reçu</ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productReceivedDetails.map(
                                                (
                                                    productDetail: SupplyOrderProductReceivedDetail,
                                                    productDetailIndex: number,
                                                ) => {
                                                    const { brokenItems, colis, conditionning, expiryDate, quantity } =
                                                        productDetail;
                                                    return (
                                                        <ReceivedDetailsRow key={productDetailIndex}>
                                                            <ReceivedDetailsCell>
                                                                <BasicInput
                                                                    value={quantity}
                                                                    onChange={({ target }) => {
                                                                        const newQuantity = parseInt(target.value) || 0;

                                                                        onDetailChange({
                                                                            productIdToUpdate: productId,
                                                                            productDetailIndex,
                                                                            productDetail: {
                                                                                ...productDetail,
                                                                                quantity: newQuantity,
                                                                            },
                                                                        });
                                                                    }}
                                                                    $centerText
                                                                    width="100%"
                                                                    step="1"
                                                                    min="0"
                                                                    required
                                                                />
                                                            </ReceivedDetailsCell>
                                                            <ReceivedDetailsCell>
                                                                <BasicInput
                                                                    value={brokenItems}
                                                                    onChange={({ target }) => {
                                                                        const newBrokenItems =
                                                                            parseInt(target.value) || 0;

                                                                        onDetailChange({
                                                                            productIdToUpdate: productId,
                                                                            productDetailIndex,
                                                                            productDetail: {
                                                                                ...productDetail,
                                                                                brokenItems: newBrokenItems,
                                                                            },
                                                                        });
                                                                    }}
                                                                    $centerText
                                                                    width="100%"
                                                                    step="1"
                                                                    min="0"
                                                                    required
                                                                />
                                                            </ReceivedDetailsCell>
                                                            <ReceivedDetailsCell>{`${colis} (+${
                                                                conditionning ? quantity % conditionning : 0
                                                            }p)`}</ReceivedDetailsCell>
                                                            <ReceivedDetailsCell>
                                                                <ReactDatePicker
                                                                    selected={
                                                                        expiryDate ? dateFromString(expiryDate) : null
                                                                    }
                                                                    onChange={(date: any) => {
                                                                        if (Array.isArray(date)) return;

                                                                        const newExpiryDate = date
                                                                            ? formatDateAsAnniversary({
                                                                                  dateTime: date,
                                                                              })
                                                                            : null;

                                                                        onDetailChange({
                                                                            productIdToUpdate: productId,
                                                                            productDetailIndex,
                                                                            productDetail: {
                                                                                ...productDetail,
                                                                                expiryDate: newExpiryDate,
                                                                            },
                                                                        });
                                                                    }}
                                                                    placeholderText="DLC"
                                                                    locale="fr"
                                                                    isClearable
                                                                    dateFormat="dd/MM/yyyy"
                                                                    customInput={<BasicInput />}
                                                                />
                                                            </ReceivedDetailsCell>
                                                            <ReceivedDetailsCell>
                                                                <BasicInput
                                                                    value={conditionning}
                                                                    onChange={({ target }) => {
                                                                        const newConditionning =
                                                                            parseInt(target.value) || 1;

                                                                        onDetailChange({
                                                                            productIdToUpdate: productId,
                                                                            productDetailIndex,
                                                                            productDetail: {
                                                                                ...productDetail,
                                                                                conditionning: newConditionning,
                                                                            },
                                                                        });
                                                                    }}
                                                                    $centerText
                                                                    width="100%"
                                                                    step="1"
                                                                    min="1"
                                                                    required
                                                                />
                                                            </ReceivedDetailsCell>
                                                            <ReceivedDetailsCell>
                                                                <ThinButton
                                                                    onClick={() =>
                                                                        removeDetail({
                                                                            productIdToUpdate: productId,
                                                                            productDetailIndex,
                                                                        })
                                                                    }
                                                                >
                                                                    <FaTrash size={13} />
                                                                </ThinButton>
                                                            </ReceivedDetailsCell>
                                                        </ReceivedDetailsRow>
                                                    );
                                                },
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <ReceivedDetailsFooterRow>
                                                <ReceivedDetailsHeaderCell>
                                                    {quantityReceived}
                                                </ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell>
                                                    {brokenItemsReceived}
                                                </ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell>{colisReceived}</ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell />
                                                <ReceivedDetailsHeaderCell>
                                                    {conditionningReceived}
                                                </ReceivedDetailsHeaderCell>
                                                <ReceivedDetailsHeaderCell />
                                            </ReceivedDetailsFooterRow>
                                        </tfoot>
                                    </ReceivedDetailsTable>
                                ) : null}
                                <ButtonRow>
                                    <ThinButton onClick={() => addDetail(productId)}>Ajouter une DLC</ThinButton>
                                </ButtonRow>
                            </ProductContainer>
                        );
                    })}
                </>
                {productsWithAdditionalQuantities.length ? (
                    <>
                        <TextContainer>
                            NB: Après l'enregistrement des quantités additionnelles, elles vont être comptées dans le
                            stock de produit dans l'entrepôt
                            <br />
                            et utilisées dans les calculs de stock disponible. Veuillez donc les ranger sans délai, afin
                            d'éviter des soldouts.
                        </TextContainer>
                        <TotemPrimaryButton
                            type="button"
                            onClick={handleProductDetailsUpdate}
                            disabled={shouldPreventUpdate || loading}
                            requiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                            $minWidth="335px"
                        >
                            {loading ? <Loader size="20px" mode={LoaderModeType.Spin} /> : 'Enregistrer les quantités'}
                        </TotemPrimaryButton>
                    </>
                ) : null}
            </Container>
        </TotemPopup>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const ReceivedDetailsTable = styled.table`
    margin-top: 10px;
    border-collapse: collapse;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border: ${({ theme }) => `1px solid ${theme.lightBorderColor}`};
`;

const ProductContainer = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    flex: 1;
    padding: 5px 10px;
    width: 100%;
    border-radius: 4px;
    border: 1px solid grey;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 5px;
`;

const ThinButton = styled(TotemPrimaryButton)`
    padding: 8px;
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 35px;
    height: 35px;
    border-radius: 35px;
    background-color: ${({ theme }) => theme.ctaPrimaryColor};
    cursor: pointer;
`;

const ReceivedDetailsHeaderCell = styled.th`
    padding: 10px;
    font-weight: 800;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-align: center;
`;

const ReceivedDetailsRow = styled.tr`
    border-top: ${({ theme }) => `1px solid ${theme.lightBorderColor}`};
`;

const ReceivedDetailsCell = styled.td`
    padding: 15px;
    color: ${({ theme }) => theme.textColor};
`;

const ReceivedDetailsFooterRow = styled.tr`
    border-top: ${({ theme }) => `1px solid ${theme.lightBorderColor}`};
`;

const TextContainer = styled.div`
    text-align: center;
`;
